import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Select from "@/components/base/Select";
import Toggle from "@/components/base/Toggle";
import Badge from "@/components/base/Badge";
import { useProvider } from "@/hooks/useProvider";
import { supabase } from "@/lib/supabase";
import { fetchNotifications } from "@/lib/data";
import { banks, notificationSettings } from "@/mocks/settings";
import type { NotificationSetting } from "@/mocks/settings";
import WorkingHoursTab from "./components/WorkingHoursTab";

const bankOptions = banks.map((b) => ({ value: b, label: b }));
const durationOptions = [
  { value: "5 دقائق", label: "5 دقائق" },
  { value: "10 دقائق", label: "10 دقائق" },
  { value: "15 دقيقة", label: "15 دقيقة" },
];
const tabs = [
  { key: "company", label: "البيانات", icon: "ri-building-4-line" },
  { key: "hours", label: "أوقات العمل", icon: "ri-time-line" },
  { key: "bank", label: "البنك", icon: "ri-bank-line" },
  { key: "orders", label: "الطلبات", icon: "ri-list-check-3" },
  { key: "notifications", label: "الإشعارات", icon: "ri-notification-3-line" },
];

function ReadOnlyField({ label, value, ltr }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-ghasla-text-primary">{label}</label>
      <div className="relative">
        <div
          dir={ltr ? "ltr" : undefined}
          className="flex h-12 w-full items-center rounded-[14px] border border-[#E4F5F5] bg-[#F0FDFD] pl-10 pr-4 text-sm text-ghasla-text-primary"
        >
          <span className="truncate">{value || "—"}</span>
        </div>
        <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ghasla-text-secondary" />
      </div>
    </div>
  );
}

export default function Settings() {
  const { provider, loading: providerLoading } = useProvider();

  // Active tab
  const [activeTab, setActiveTab] = useState("company");

  // Branding (editable)
  const [description, setDescription] = useState("");
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [email, setEmail] = useState("");

  // Bank
  const [bankName, setBankName] = useState("مصرف الراجحي");
  const [iban, setIban] = useState("");
  const [accountName, setAccountName] = useState("");
  const [bankRequested, setBankRequested] = useState(false);

  // Order settings
  const [autoAccept, setAutoAccept] = useState(false);
  const [acceptDuration, setAcceptDuration] = useState("10 دقائق");

  // Notifications
  const [notifications, setNotifications] = useState<NotificationSetting[]>([]);

  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load provider email (from auth account)
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setEmail(data.user?.email ?? ""));
  }, []);

  // Load provider + child data
  useEffect(() => {
    setDescription(provider?.description ?? "");
    setBankName(provider?.bank_name || "مصرف الراجحي");
    setIban(provider?.bank_iban ?? "SA44 2000 0001 2345 6789 1234");
    setAccountName(provider?.bank_account_name ?? "شركة الماية للخدمات");
    setAutoAccept(provider?.auto_accept ?? false);
    setAcceptDuration(provider?.accept_duration || "10 دقائق");

    setLoading(true);
    if (!provider) {
      // وضع المعاينة: عرض بيانات تجريبية حتى لا تعلق الصفحة على التحميل
      setNotifications(notificationSettings);
      setLoading(false);
      return;
    }
    fetchNotifications(provider.id)
      .then(setNotifications)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [provider]);

  const updateProviderField = async (fields: Record<string, unknown>) => {
    if (!provider) return;
    await supabase.from("providers").update(fields).eq("id", provider.id);
  };

  const handleSaveBranding = async () => {
    await updateProviderField({ description });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleRequestBank = async () => {
    await updateProviderField({ bank_name: bankName, bank_iban: iban, bank_account_name: accountName });
    setBankRequested(true);
    setTimeout(() => setBankRequested(false), 4000);
  };

  const updateNotification = (id: number, field: "push" | "sms") => {
    setNotifications((prev) => {
      const next = prev.map((n) => (n.id === id ? { ...n, [field]: !n[field] } : n));
      const target = next.find((n) => n.id === id);
      if (target) {
        supabase.from("notification_settings").update({ [field]: target[field] }).eq("id", id);
      }
      return next;
    });
  };

  const inputClass =
    "h-12 w-full rounded-[14px] border border-[#E4F5F5] bg-[#F0FDFD] px-4 text-sm text-ghasla-text-heading outline-none transition-colors focus:border-[#00AAB7]";
  const labelClass = "mb-2 block text-sm font-medium text-ghasla-text-primary";

  if (providerLoading || loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#E4F5F5] border-t-[#0D6E6E]" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl">
      <h1 className="text-[28px] font-bold text-ghasla-text-heading">الإعدادات</h1>

      {/* Tab bar */}
      <div className="mt-6 flex flex-wrap gap-2 rounded-2xl border border-[#E4F5F5] bg-white p-1.5 shadow-card">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveTab(t.key)}
            className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === t.key
                ? "bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] text-white"
                : "text-ghasla-text-primary hover:bg-[#F0FDFD]"
            }`}
          >
            <i className={`${t.icon} text-base`} />
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5 flex flex-col gap-5">
        {/* ===== بيانات ===== */}
        {activeTab === "company" && (
          <>
            {/* 1. Company data (read-only) */}
            <section className="rounded-[20px] bg-white p-6 shadow-card">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-ghasla-text-heading">بيانات الشركة</h2>
                <Badge tone="gray">عرض فقط</Badge>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <ReadOnlyField label="اسم الشركة" value={provider?.company_name ?? "شركة الماية للخدمات"} />
                <ReadOnlyField label="رقم السجل التجاري" value={provider?.commercial_reg ?? "1010456789"} ltr />
                <ReadOnlyField label="المدينة" value={provider?.city ?? "الرياض"} />
                <ReadOnlyField label="رقم الجوال" value={provider?.phone ?? "0551234567"} ltr />
                <div className="sm:col-span-2">
                  <ReadOnlyField label="البريد الإلكتروني" value={email} ltr />
                </div>
              </div>
              <div className="mt-6 flex flex-col gap-3 rounded-[14px] bg-[#FFF9EB] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="flex items-center gap-2 text-sm text-[#B45309]">
                  <i className="ri-information-line text-base" />
                  لتعديل البيانات الأساسية تواصل مع الإدارة
                </p>
                <Link
                  to="/provider/support"
                  className="inline-flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                >
                  تواصل مع الإدارة
                  <i className="ri-arrow-left-line" />
                </Link>
              </div>
            </section>

            {/* 2. Logo & description (editable) */}
            <section className="rounded-[20px] bg-white p-6 shadow-card">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-ghasla-text-heading">الشعار والوصف</h2>
                <Badge tone="green">قابل للتعديل</Badge>
              </div>
              <div className="mt-6 space-y-5">
                <div>
                  <label className={labelClass}>الشعار</label>
                  <div className="flex items-center gap-4">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="شعار الشركة"
                        className="h-14 w-14 rounded-2xl object-cover"
                      />
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#0D6E6E] to-[#3CBCB4] text-lg font-bold text-white">
                        {provider?.company_name?.slice(0, 2) || "شر"}
                      </div>
                    )}
                    <label className="cursor-pointer text-sm font-medium text-ghasla-text-link hover:underline">
                      تغيير الصورة
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) setLogoPreview(URL.createObjectURL(file));
                        }}
                      />
                    </label>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>الوصف المختصر</label>
                  <textarea
                    className="min-h-[100px] w-full rounded-[14px] border border-[#E4F5F5] bg-[#F0FDFD] px-4 py-3 text-sm text-ghasla-text-heading outline-none transition-colors focus:border-[#00AAB7]"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleSaveBranding}
                className="mt-6 cursor-pointer rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                حفظ التغييرات
              </button>
              {saved && (
                <span className="mr-3 inline-flex items-center gap-1 text-sm font-medium text-[#27AE60]">
                  <i className="ri-checkbox-circle-fill" /> تم الحفظ
                </span>
              )}
            </section>
          </>
        )}

        {/* ===== أوقات العمل ===== */}
        {activeTab === "hours" && <WorkingHoursTab provider={provider} />}

        {/* ===== البنك ===== */}
        {activeTab === "bank" && (
          <section className="rounded-[20px] border-l-4 border-[#F59E0B] bg-white p-6 shadow-card">
            <h2 className="text-xl font-semibold text-ghasla-text-heading">الحساب البنكي</h2>
            <div className="mt-2 flex items-center gap-2 text-xs text-[#F59E0B]">
              <i className="ri-lock-line" />
              أي تعديل يحتاج مراجعة من الإدارة قبل التفعيل
            </div>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div>
                <label className={labelClass}>البنك</label>
                <Select value={bankName} onChange={setBankName} options={bankOptions} />
              </div>
              <div>
                <label className={labelClass}>رقم الآيبان IBAN</label>
                <input className={inputClass} value={iban} onChange={(e) => setIban(e.target.value)} dir="ltr" />
              </div>
              <div>
                <label className={labelClass}>اسم صاحب الحساب</label>
                <input className={inputClass} value={accountName} onChange={(e) => setAccountName(e.target.value)} />
              </div>
            </div>
            <button
              type="button"
              onClick={handleRequestBank}
              className="mt-6 cursor-pointer rounded-xl border border-[#F59E0B] px-6 py-3 text-sm font-semibold text-[#B45309] transition-colors hover:bg-[#FFF9EB]"
            >
              طلب تعديل
            </button>
            {bankRequested && (
              <p className="mt-3 text-sm text-[#27AE60]">
                تم إرسال طلب التعديل — بنراجعه ونفعّله خلال يوم عمل
              </p>
            )}
          </section>
        )}

        {/* ===== الطلبات ===== */}
        {activeTab === "orders" && (
          <section className="rounded-[20px] bg-white p-6 shadow-card">
            <h2 className="text-xl font-semibold text-ghasla-text-heading">إعدادات الطلبات</h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className={labelClass}>مدة القبول</label>
                <Select
                  value={acceptDuration}
                  onChange={(v) => {
                    setAcceptDuration(v);
                    updateProviderField({ accept_duration: v });
                  }}
                  options={durationOptions}
                />
              </div>
              <div className="flex items-end">
                <div className="flex w-full items-center justify-between rounded-[14px] bg-[#F0FDFD] px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-ghasla-text-primary">قبول تلقائي</p>
                    <p className="text-xs text-ghasla-text-secondary">الطلبات تتقبل تلقائي بدون مراجعة</p>
                  </div>
                  <Toggle
                    checked={autoAccept}
                    onChange={(v) => {
                      setAutoAccept(v);
                      updateProviderField({ auto_accept: v });
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ===== الإشعارات ===== */}
        {activeTab === "notifications" && (
          <section className="rounded-[20px] bg-white p-6 shadow-card">
            <h2 className="text-xl font-semibold text-ghasla-text-heading">الإشعارات</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="text-xs font-semibold text-ghasla-text-secondary">
                    <th className="h-11 px-3 text-right">الإشعار</th>
                    <th className="h-11 px-3 text-center">Push</th>
                    <th className="h-11 px-3 text-center">SMS</th>
                    <th className="h-11 px-3 text-center">التفعيل</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((n) => (
                    <tr key={n.id} className="border-t border-[#F4F8F8]">
                      <td className="h-12 px-3 text-ghasla-text-primary">{n.label}</td>
                      <td className="h-12 px-3">
                        <div className="flex justify-center">
                          <Toggle
                            size="sm"
                            checked={n.push}
                            disabled={n.mandatory}
                            onChange={() => updateNotification(n.id, "push")}
                          />
                        </div>
                      </td>
                      <td className="h-12 px-3">
                        <div className="flex justify-center">
                          <Toggle
                            size="sm"
                            checked={n.sms}
                            disabled={n.mandatory}
                            onChange={() => updateNotification(n.id, "sms")}
                          />
                        </div>
                      </td>
                      <td className="h-12 px-3">
                        {n.mandatory ? (
                          <span className="inline-flex justify-center text-xs text-ghasla-text-secondary">إجباري</span>
                        ) : (
                          <div className="flex justify-center">
                            <Toggle size="sm" checked onChange={() => {}} />
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

      </div>
    </div>
  );
}